/**
 * SingleHousePage — V14.62 Sprint 22 (Mode Maison Unifamiliale).
 *
 * Route : /maison/:houseId
 *
 * Orchestre l'édition étage-par-étage d'une maison individuelle :
 *   • Header : nom maison + client + adresse + actions (sauvegarder, retour)
 *   • Tabs : un onglet par étage (Sous-sol / RDC / R+1 / Combles…)
 *   • Body : EditorToolbar + HybridEditor + DataStructurePanel du Lab
 *   • Persistance auto à chaque switch d'étage (sauvegarde l'étage qu'on quitte)
 *
 * Le store Zustand existant est REUTILISÉ tel quel. À chaque changement
 * d'étage on appelle `useGeometryStore.loadFromMicroIFC(...)` pour
 * réinitialiser l'éditeur sur la géométrie de l'étage cible.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Home, Loader2, AlertCircle, Building2, Layers, Satellite,
  Box, Maximize2, Layers3,
} from 'lucide-react';
import { Toaster } from '../ui/sonner';
import EditorToolbar from '../lab/geometry/EditorToolbar';
import HybridEditor from '../lab/geometry/HybridEditor';
import DataStructurePanel from '../lab/geometry/DataStructurePanel';
import CalibrationModal from '../lab/geometry/CalibrationModal';
import StairsTypeModal from '../lab/geometry/StairsTypeModal';
import useKeyboardShortcuts from '../lab/geometry/useKeyboardShortcuts';
import { useGeometryStore } from '../lab/geometry/useGeometryStore';
import { useSyncMapWithLocation } from '../../contexts/MapBackgroundContext';
import CoherenceBanner from './CoherenceBanner';
import Shoebox3DViewer from '../3d/Shoebox3DViewer';
import BuildingStack3DViewer from '../3d/BuildingStack3DViewer';
import Exterior3DViewer from '../3d/Exterior3DViewer';
import FloorAlignmentEditor from '../3d/FloorAlignmentEditor';
import MaisonPdfExportButton from '../3d/MaisonPdfExportButton';
import { formatApiError } from '../../lib/formatApiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SingleHousePage = () => {
  const { houseId } = useParams();
  const navigate = useNavigate();

  const [house, setHouse] = useState(null);
  // V14.68.6 — Sync du fond map global sur les coords de la maison active.
  useSyncMapWithLocation(house?.coords, 17);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFloorId, setActiveFloorId] = useState(null);
  const [saving, setSaving] = useState(false);
  // Sprint 26F/G — 5 modes maintenant :
  //   'plans'     : éditeur 2D Konva (Édition par étage)
  //   'align'     : calage inter-étages (overlay 2D transparents)
  //   'shoebox3d' : Shoebox 3D de l'étage actif (orbit)
  //   'building3d': Empilement 3D du bâtiment complet (orbit)
  //   'exterior3d': Vue extérieure 3D orbitable (terrain + façade + caméra presets)
  const [viewMode, setViewMode] = useState('plans');
  // Bumped after every successful save → re-fetches the coherence report.
  const [coherenceRefreshKey, setCoherenceRefreshKey] = useState(0);
  // Exterior 3D payload — fetched on demand when entering exterior3d mode.
  const [exteriorPayload, setExteriorPayload] = useState(null);
  const [exteriorLoading, setExteriorLoading] = useState(false);
  // Sprint 26F — Pending alignment transforms (not yet persisted).
  const [pendingTransforms, setPendingTransforms] = useState({});
  const [alignmentSaving, setAlignmentSaving] = useState(false);

  const loadFromMicroIFC = useGeometryStore((s) => s.loadFromMicroIFC);
  const toMicroIFC = useGeometryStore((s) => s.toMicroIFC);
  useKeyboardShortcuts({ enabled: !!house });

  // ─── Fetch house ───
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/single-house/projects/${houseId}`,
          { withCredentials: true },
        );
        if (cancelled) return;
        setHouse(data);
        // Sort floors by level desc (combles top → sous-sol bottom)
        const sortedFloors = [...data.floors].sort((a, b) => b.level - a.level);
        const firstId = sortedFloors[0]?.floor_id;
        if (firstId) {
          setActiveFloorId(firstId);
          const geom = sortedFloors[0].geometry || {};
          loadFromMicroIFC(geom);
        }
      } catch (err) {
        if (!cancelled) setError(formatApiError(err, 'Maison introuvable'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [houseId]);

  // Sorted floors (top floor first)
  const orderedFloors = useMemo(
    () => (house?.floors ? [...house.floors].sort((a, b) => b.level - a.level) : []),
    [house],
  );

  const activeFloor = useMemo(
    () => orderedFloors.find((f) => f.floor_id === activeFloorId) || null,
    [orderedFloors, activeFloorId],
  );

  // ─── Save the floor we're leaving, then switch ───
  const persistAndSwitch = useCallback(async (nextFloorId) => {
    if (!activeFloor || nextFloorId === activeFloor.floor_id) return;
    setSaving(true);
    const payload = toMicroIFC();
    try {
      await axios.put(
        `${API}/single-house/projects/${houseId}/floors/${activeFloor.floor_id}/geometry`,
        payload,
        { withCredentials: true },
      );
      // Mutate in-memory house with the saved geometry
      setHouse((prev) => prev && ({
        ...prev,
        floors: prev.floors.map((f) =>
          f.floor_id === activeFloor.floor_id ? { ...f, geometry: payload } : f),
      }));
      setCoherenceRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiError(err, "Sauvegarde de l'étage impossible"));
      setSaving(false);
      return;
    }
    setSaving(false);

    const target = orderedFloors.find((f) => f.floor_id === nextFloorId);
    if (target) {
      setActiveFloorId(nextFloorId);
      loadFromMicroIFC(target.geometry || {});
    }
  }, [activeFloor, houseId, orderedFloors, toMicroIFC, loadFromMicroIFC]);

  const handleManualSave = useCallback(async () => {
    if (!activeFloor) return;
    setSaving(true);
    const payload = toMicroIFC();
    try {
      const { data } = await axios.put(
        `${API}/single-house/projects/${houseId}/floors/${activeFloor.floor_id}/geometry`,
        payload,
        { withCredentials: true },
      );
      toast.success(
        `Étage sauvegardé : ${activeFloor.name}`,
        { description: `${data.stats.walls} murs · ${data.stats.windows} fen. · ${data.stats.doors} portes · ${data.stats.stairs} esc.${data.stats.terrain_vertices ? ` · ${data.stats.terrain_vertices} pts terrain` : ''}` },
      );
      setHouse((prev) => prev && ({
        ...prev,
        floors: prev.floors.map((f) =>
          f.floor_id === activeFloor.floor_id ? { ...f, geometry: payload } : f),
      }));
      setCoherenceRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiError(err, 'Sauvegarde impossible'));
    } finally {
      setSaving(false);
    }
  }, [activeFloor, houseId, toMicroIFC]);

  // ─── Sauvegarde l'étage en cours puis bascule vers un mode "non-plans" ───
  // (utilisé par Calage / Shoebox3D / Bâtiment3D / Vue extérieure)
  const switchToMode = useCallback(async (mode) => {
    if (viewMode === mode) return;
    if (viewMode === 'plans' && activeFloor) {
      setSaving(true);
      const payload = toMicroIFC();
      try {
        await axios.put(
          `${API}/single-house/projects/${houseId}/floors/${activeFloor.floor_id}/geometry`,
          payload,
          { withCredentials: true },
        );
        setHouse((prev) => prev && ({
          ...prev,
          floors: prev.floors.map((f) =>
            f.floor_id === activeFloor.floor_id ? { ...f, geometry: { ...payload } } : f),
        }));
        setCoherenceRefreshKey((k) => k + 1);
      } catch (err) {
        toast.error(formatApiError(err, "Impossible d'enregistrer l'étage en cours"));
        setSaving(false);
        return;
      }
      setSaving(false);
    }
    // Sprint 26F polish — when entering Shoebox 3D, auto-switch to the first
    // floor that actually has walls so the viewer is not stuck on the empty
    // "Aucun mur tracé" fallback (e.g. Combles by default).
    if (mode === 'shoebox3d') {
      const currentFloor = (house?.floors || []).find((f) => f.floor_id === activeFloorId);
      const currentHasWalls = (currentFloor?.geometry?.walls?.length || 0) > 0;
      if (!currentHasWalls) {
        const firstWithWalls = (house?.floors || []).find(
          (f) => (f.geometry?.walls?.length || 0) > 0);
        if (firstWithWalls) setActiveFloorId(firstWithWalls.floor_id);
      }
    }
    setViewMode(mode);
  }, [viewMode, activeFloor, houseId, toMicroIFC, house, activeFloorId]);

  // ─── Fetch exterior payload at mount (and on coherence refresh).
  // Used by both the "Vue extérieure 3D" tab AND the PDF export button in
  // the header, which is always visible. ───
  useEffect(() => {
    let cancelled = false;
    setExteriorLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/single-house/projects/${houseId}/exterior`,
          { withCredentials: true },
        );
        if (!cancelled) setExteriorPayload(data);
      } catch (err) {
        // Silent — exterior may legitimately be empty for fresh projects.
        if (!cancelled) setExteriorPayload(null);
      } finally {
        if (!cancelled) setExteriorLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [houseId, coherenceRefreshKey]);

  // ─── Sprint 26F — Apply pending transforms locally + persist on commit ───
  const mergedFloors = useMemo(() => {
    if (!house?.floors) return [];
    return house.floors.map((f) => {
      const pending = pendingTransforms[f.floor_id];
      if (!pending) return f;
      return { ...f, geometry: { ...(f.geometry || {}), transform: pending } };
    });
  }, [house, pendingTransforms]);

  const handleChangeTransform = useCallback((floorId, transform) => {
    setPendingTransforms((prev) => ({ ...prev, [floorId]: transform }));
  }, []);

  const handleCommitAlignment = useCallback(async () => {
    if (!Object.keys(pendingTransforms).length) {
      toast.info('Aucun calage à sauvegarder');
      return;
    }
    setAlignmentSaving(true);
    try {
      await axios.put(
        `${API}/single-house/projects/${houseId}/alignment`,
        { transforms: pendingTransforms },
        { withCredentials: true },
      );
      // Merge transforms into house in-place so they survive the next reload.
      setHouse((prev) => prev && ({
        ...prev,
        floors: prev.floors.map((f) => pendingTransforms[f.floor_id]
          ? { ...f, geometry: { ...(f.geometry || {}), transform: pendingTransforms[f.floor_id] } }
          : f),
      }));
      setPendingTransforms({});
      toast.success('Calage sauvegardé');
    } catch (err) {
      toast.error(formatApiError(err, 'Sauvegarde du calage impossible'));
    } finally {
      setAlignmentSaving(false);
    }
  }, [pendingTransforms, houseId]);

  // ─── States ───
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-300"
           style={{ background: '#050a13' }}>
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Chargement de la maison…
      </div>
    );
  }
  if (error || !house) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-gray-300"
           style={{ background: '#050a13' }}>
        <AlertCircle className="w-6 h-6 text-amber-400 mb-2" />
        <p className="text-sm">{error || 'Maison introuvable'}</p>
        <button type="button" onClick={() => navigate('/projects')}
                className="mt-4 px-3 py-1.5 rounded border border-white/15 text-[11px] hover:bg-white/5">
          Retour aux projets
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white"
         style={{
           background: 'radial-gradient(circle at 80% 0%, rgba(16,185,129,0.06), transparent 50%), #050a13',
         }}
         data-testid="single-house-page">

      {/* HEADER */}
      <header className="px-6 py-3 border-b flex items-center gap-4"
              style={{ borderColor: 'rgba(16,185,129,0.18)', background: 'rgba(2,6,12,0.6)' }}>
        <Link to="/projects"
              className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-emerald-300"
              data-testid="house-back-link">
          <ArrowLeft className="w-3.5 h-3.5" />
          Projets
        </Link>
        <div className="h-4 w-px bg-white/10" />
        <Home className="w-4 h-4 text-emerald-400" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300/80 leading-none">
            Maison Unifamiliale
          </div>
          <div className="text-sm font-semibold mt-0.5 truncate" data-testid="house-name">
            {house.name}
            {house.client_name && <span className="text-gray-400 font-normal"> — {house.client_name}</span>}
          </div>
          {house.address && (
            <div className="text-[10px] text-gray-500 truncate">{house.address}</div>
          )}
        </div>
        <button type="button"
                onClick={handleManualSave}
                disabled={saving || viewMode !== 'plans'}
                data-testid="house-save-floor-btn"
                className={`inline-flex items-center gap-1.5 h-8 px-3 rounded text-[11px] font-semibold bg-emerald-500 hover:bg-emerald-400 text-emerald-950 disabled:opacity-40 disabled:cursor-not-allowed ${viewMode === 'plans' ? '' : 'hidden'}`}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {saving ? 'Sauvegarde…' : 'Sauvegarder l\'étage'}
        </button>
        {/* Sprint 27 — Dossier PDF Flo X.
            Visible en permanence dans le header pour permettre le téléchargement
            sans avoir à naviguer dans les onglets de mode 3D. */}
        <MaisonPdfExportButton
          houseId={houseId}
          title={house.name}
          address={house.address}
          subtitle={house.client_name ? `Pour ${house.client_name}` : ''}
          floors={mergedFloors}
          exteriorPayload={exteriorPayload}
          testId="house-pdf-export-btn"
        />
      </header>

      {/* MODE SWITCHER — Plans · Calage · Shoebox 3D · Bâtiment 3D · Vue extérieure 3D */}
      <nav className="px-6 py-2 border-b flex items-center gap-1.5 overflow-x-auto"
           style={{ borderColor: 'rgba(16,185,129,0.12)', background: 'rgba(2,6,12,0.4)' }}
           data-testid="house-mode-tabs">
        <span className="text-[9.5px] uppercase tracking-[0.25em] text-emerald-300/60 mr-2 shrink-0">
          Mode
        </span>
        {[
          { id: 'plans',      label: 'Plans par étage',   icon: Layers,    testid: 'house-mode-plans-btn' },
          { id: 'align',      label: 'Calage étages',      icon: Maximize2, testid: 'house-mode-align-btn' },
          { id: 'shoebox3d',  label: 'Shoebox 3D',         icon: Box,       testid: 'house-mode-shoebox3d-btn' },
          { id: 'building3d', label: 'Bâtiment 3D',        icon: Layers3,   testid: 'house-mode-building3d-btn' },
          { id: 'exterior3d', label: 'Vue extérieure 3D',  icon: Satellite, testid: 'house-mode-exterior3d-btn' },
        ].map((m) => {
          const Icon = m.icon;
          const active = viewMode === m.id;
          return (
            <button key={m.id}
                    type="button"
                    onClick={() => switchToMode(m.id)}
                    disabled={saving}
                    aria-pressed={active}
                    data-testid={m.testid}
                    className={`shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded text-[11px] border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                      active
                        ? 'bg-emerald-500/20 border-emerald-400/70 text-emerald-100'
                        : 'bg-black/30 border-white/10 text-gray-300 hover:bg-white/5 hover:border-emerald-400/30'
                    }`}>
              <Icon className="w-3.5 h-3.5" />
              {m.label}
            </button>
          );
        })}
        <div className="flex-1" />
        <span className="text-[10px] text-gray-500 font-mono whitespace-nowrap shrink-0">
          {viewMode === 'plans'      && 'Tracez murs, terrain (T), escaliers, fenêtres, portes — calibrez avec la Règle (C)'}
          {viewMode === 'align'      && 'Empilez les plans avec transparence — alignez tous les étages sur le RDC ancré'}
          {viewMode === 'shoebox3d'  && 'Vue 3D orbitable de l\'étage actif (souris : rotate / pan / zoom)'}
          {viewMode === 'building3d' && 'Empilement vertical des étages — alignements appliqués'}
          {viewMode === 'exterior3d' && 'Vue extérieure photoréaliste — caméra orbitable + presets + export PNG'}
        </span>
      </nav>

      {/* FLOOR TABS — relevant for plans (editor) AND shoebox3d (per-floor 3D) */}
      {(viewMode === 'plans' || viewMode === 'shoebox3d') && (
      <nav className="px-6 py-2 border-b flex items-center gap-1 overflow-x-auto"
           style={{ borderColor: 'rgba(16,185,129,0.12)', background: 'rgba(2,6,12,0.4)' }}
           data-testid="house-floor-tabs">
        <Building2 className="w-3.5 h-3.5 text-emerald-400/70 mr-2 shrink-0" />
        {orderedFloors.map((f) => {
          const active = f.floor_id === activeFloorId;
          const hasGeometry = (f.geometry?.walls?.length || 0) > 0;
          const isCalibrated = (f.geometry?.scale_m_per_px || 0) > 0;
          return (
            <button
              key={f.floor_id}
              type="button"
              onClick={() => viewMode === 'plans'
                ? persistAndSwitch(f.floor_id)
                : setActiveFloorId(f.floor_id)}
              data-testid={`house-floor-tab-${f.code}`}
              aria-pressed={active}
              className={`inline-flex items-center gap-1.5 h-8 px-3 rounded text-[11px] border transition-colors shrink-0 ${
                active
                  ? 'bg-emerald-500/20 border-emerald-400/70 text-emerald-100'
                  : 'bg-black/30 border-white/10 text-gray-300 hover:bg-white/5 hover:border-emerald-400/30'
              }`}
            >
              <Layers className="w-3 h-3" />
              <span>{f.name}</span>
              <span className="font-mono text-[9.5px] opacity-60">{f.code}</span>
              {hasGeometry && (
                <span className={`w-1.5 h-1.5 rounded-full ${isCalibrated ? 'bg-emerald-400' : 'bg-amber-400'}`}
                      title={isCalibrated ? 'Calibré' : 'Non calibré'} />
              )}
            </button>
          );
        })}
        <div className="flex-1" />
        <span className="text-[10px] text-gray-500 font-mono whitespace-nowrap" data-testid="active-floor-height">
          {activeFloor ? `H = ${activeFloor.height_m.toFixed(2)} m` : ''}
        </span>
      </nav>
      )}

      {/* MAIN BODY */}
      <main className="px-6 py-4 space-y-4">
        <CoherenceBanner houseId={houseId} refreshKey={coherenceRefreshKey} />

        {viewMode === 'plans' && (
          <>
            <EditorToolbar />
            <div className="flex gap-4 items-start">
              <HybridEditor />
              <DataStructurePanel />
            </div>
            <div className="text-[10.5px] text-gray-500 max-w-[820px] leading-relaxed">
              <strong className="text-emerald-300">Cohérence verticale Sprint 23 :</strong>{' '}
              chaque escalier de cet étage est automatiquement testé contre les
              escaliers des étages adjacents (recouvrement géométrique ≥ 15%). Les
              orphelins et incohérences d&apos;échelle sont remontés dans le
              bandeau ci-dessus en temps réel après chaque sauvegarde.
            </div>
          </>
        )}

        {viewMode === 'align' && (
          <FloorAlignmentEditor
            floors={mergedFloors}
            activeFloorId={activeFloorId}
            onSelectFloor={setActiveFloorId}
            onChangeTransform={handleChangeTransform}
            onCommit={handleCommitAlignment}
            saving={alignmentSaving}
          />
        )}

        {viewMode === 'shoebox3d' && (
          <div className="rounded-lg border border-white/10 overflow-hidden"
               style={{ height: 640 }}
               data-testid="house-shoebox3d-container">
            <Shoebox3DViewer
              floor={activeFloor}
              height={activeFloor?.height_m}
              showGrid
              showTerrain
              className="w-full h-full"
              testId="house-shoebox3d-viewer"
            />
          </div>
        )}

        {viewMode === 'building3d' && (
          <div className="rounded-lg border border-white/10 overflow-hidden"
               style={{ height: 640 }}
               data-testid="house-building3d-container">
            <BuildingStack3DViewer
              floors={mergedFloors}
              activeFloorId={activeFloorId}
              showGrid
              className="w-full h-full"
              testId="house-building3d-viewer"
            />
          </div>
        )}

        {viewMode === 'exterior3d' && (
          <div className="rounded-lg border border-white/10 overflow-hidden"
               style={{ height: 640 }}
               data-testid="house-exterior3d-container">
            {exteriorLoading && (
              <div className="w-full h-full flex items-center justify-center text-gray-300 text-[12px]">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Calcul de la vue extérieure…
              </div>
            )}
            {!exteriorLoading && exteriorPayload && (
              <Exterior3DViewer
                exteriorPayload={exteriorPayload}
                title={house?.name || 'Vue extérieure 3D'}
                subtitle={[
                  house?.address,
                  exteriorPayload?.terrain?.area_m2
                    ? `Terrain ${exteriorPayload.terrain.area_m2.toFixed(0)} m²`
                    : null,
                  exteriorPayload?.footprint?.surface_m2
                    ? `Bâti ${exteriorPayload.footprint.surface_m2.toFixed(0)} m²`
                    : null,
                ].filter(Boolean).join(' · ')}
                className="w-full h-full"
                testId="house-exterior3d-viewer"
              />
            )}
            {!exteriorLoading && !exteriorPayload && (
              <div className="w-full h-full flex items-center justify-center text-gray-400 text-[12px]">
                Vue extérieure indisponible
              </div>
            )}
          </div>
        )}
      </main>

      <CalibrationModal />
      <StairsTypeModal />
      <Toaster position="top-right" richColors />
    </div>
  );
};

export default SingleHousePage;
